import { type RecordId } from 'javascripts/shared-types';
import ApplicationModel, { attr } from '../ApplicationModel';
import DateRangeFilter, {
  type DateRangeFilterValue,
} from '../filters/DateRangeFilter';
import EstimateTypeFilter, {
  type EstimateTypeFilterValue,
} from '../filters/EstimateTypeFilter';
import type ItemFilter from '../filters/ItemFilter';
import TimePeriodFilter from '../filters/TimePeriodFilter';
import UserFilter from '../filters/UserFilter';
import ValueFilter from '../filters/ValueFilter';
import { nameMap } from '../lib/nameMap';
import { PARSE_ITERATION_RECORD_EVENT_TYPES } from '../lib/RecordEvents/parseRecordData';
import recordEvents from '../lib/RecordEvents/recordEvents';
import { model } from '../modelBuilder';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { type BookmarksVelocityChartAttributes } from './generated/BookmarksVelocityChartAttributes';
import User from './User';

interface TimePeriod {
  unit: 'day' | 'week' | 'month' | 'year';
  value: number;
}

interface Filters {
  timePeriod: TimePeriod; // Legacy, we'll convert from this if set
  dateRange: DateRangeFilterValue;
  users?: RecordId[];
  excludeActiveIteration?: boolean;
  estimationType?: EstimateTypeFilterValue[];
}

interface Local {
  filterObjects: Record<string, ItemFilter<any>>;
  users?: User[];
}

interface BookmarksVelocityChart
  extends BookmarksVelocityChartAttributes,
    HasBookmarkFilters<{
      dateRange: DateRangeFilter;
      users: UserFilter;
      excludeActiveIteration: ValueFilter<boolean>;
      estimationType: EstimateTypeFilter;
    }> {
  local: Local;
  filters: Filters;
}

@model
@HasBookmarkFilters
class BookmarksVelocityChart extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksVelocityChart' = 'BookmarksVelocityChart';
  get typename(): 'BookmarksVelocityChart' {
    return 'BookmarksVelocityChart';
  }

  static internalTypename: 'Bookmarks::VelocityChart' =
    'Bookmarks::VelocityChart';
  get internalTypename(): 'Bookmarks::VelocityChart' {
    return 'Bookmarks::VelocityChart';
  }

  static bookmarkName = 'Bookmarks::VelocityChart';

  static inputType = 'VelocityChartAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  initialize() {
    this.local = {
      filterObjects: {},
    };
    this.filters = this.filters || {
      timePeriod: { unit: 'week', value: 6 },
      excludeActiveIteration: false,
    };
  }

  /**
   * Initialize the filters
   */
  async createFilters() {
    const users = await this.availableUsers();

    const userFilter = new UserFilter(
      this.filters.users || [],
      users.map(nameMap)
    );

    const defaultCapacityUnits = this.project?.defaultCapacityUnits || 'POINTS';

    const estimateTypeFilter = new EstimateTypeFilter({
      initialItems: this.filters.estimationType || ['originalEstimate'],
      capacityUnits: defaultCapacityUnits,
    });

    const timePeriod = new TimePeriodFilter(this.filters.timePeriod);
    let dateRangeFilter: DateRangeFilter;

    // Convert from legacy timePeriod filter
    if (!this.filters.dateRange && this.filters.timePeriod) {
      dateRangeFilter = timePeriod.asDateRangeFilter();
    } else {
      dateRangeFilter = new DateRangeFilter(this.filters.dateRange, false);
    }

    return {
      users: userFilter,
      dateRange: dateRangeFilter,
      excludeActiveIteration: new ValueFilter(
        this.filters.excludeActiveIteration || false
      ),
      estimationType: estimateTypeFilter,
    };
  }

  canFilter(_scope) {
    return true;
  }

  /**
   * Load all the users available to this team
   */
  async availableUsers() {
    if (!this.local.users)
      this.local.users = await User.select('id', 'name', 'initials', 'email')
        .select({ avatarUrl: { size: aha.enums.AvatarSizeEnum.SIZE_40 } })
        .where({ projectId: this.projectId })
        .per(100)
        .all();
    return this.local.users;
  }

  /**
   * Load the selected user
   */
  async user() {
    // @ts-ignore filters are not yet typed
    const userId = this.getFilter('users').single();
    if (!userId) return null;
    const users = await this.availableUsers();
    return users.find(u => u.id === userId);
  }

  async rawEvents(filters: any) {
    const rawEvents = await recordEvents.recordEventsRaw({
      eventType: PARSE_ITERATION_RECORD_EVENT_TYPES,
      teamId: this.projectId,
      ...filters,
    });

    return rawEvents.filter(event => event.iterationId);
  }
}

export default BookmarksVelocityChart;
